import React, { useEffect, useMemo, useState } from 'react';
import { Plus, X, ChevronDown, Loader2 } from 'lucide-react';
import { getFilterSchema, getPropertyValues } from 'features/agents/api/agentsApi';
import { SelectField, cx, inputCls, ghostBtnCls, removeBtnCls } from 'features/agents/WorkflowEditor/fields/inputs';

/**
 * FilterBuilder — entity-aware filter UI matching the evals/insights pattern.
 *
 * Props:
 *   entity     'events' | 'runs' | 'spans' | 'users'
 *   eventName  string?    (events only — narrows the property catalog)
 *   value      { match: 'AND'|'OR', clauses: [{ field, op, value }] }
 *   onChange   (next) => void
 *
 * When entity='events' we also render an Event picker row at the top so the
 * trigger can be scoped to a specific event_name. The picker writes to the
 * `event_name` clause directly so the filter contract stays a single shape.
 */
// Tokens only, so the builder reads in light and dark.
const panelCls = 'space-y-2 rounded-md border border-[color:var(--border-default)] p-2 text-[color:var(--text-primary)]';
const menuCls = 'absolute z-50 mt-1 left-0 right-0 overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] text-[color:var(--text-primary)] shadow-token-lg';
const menuItemCls = 'w-full truncate px-2.5 py-1.5 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-hover)]';

export default function FilterBuilder({ entity, value, onChange, eventName, onEventNameChange, hideEventPicker = false }) {
  const v = value && typeof value === 'object' ? value : { match: 'AND', clauses: [] };
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getFilterSchema(entity, eventName)
      .then((s) => { if (!cancelled) setSchema(s); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.message || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entity, eventName]);

  const fieldsByKey = useMemo(() => Object.fromEntries((schema?.fields || []).map((f) => [f.key, f])), [schema]);

  const updateClause = (idx, patch) => {
    const clauses = v.clauses.map((c, i) => i === idx ? { ...c, ...patch } : c);
    onChange({ ...v, clauses });
  };
  const addClause = () => {
    const def = schema?.fields?.[0]?.key || '';
    const ops = schema?.operators_by_type?.[schema?.fields?.[0]?.type || 'string'] || ['eq'];
    onChange({ ...v, clauses: [...(v.clauses || []), { field: def, op: ops[0], value: '' }] });
  };
  const removeClause = (idx) => onChange({ ...v, clauses: v.clauses.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-2">
      {entity === 'events' && !hideEventPicker && (
        <EventNamePicker
          schema={schema}
          eventName={eventName || ''}
          onChange={(name) => onEventNameChange && onEventNameChange(name)}
        />
      )}

      <div className={panelCls}>
        <div className="flex items-center gap-2 text-[12px] text-[color:var(--text-muted)]">
          <span>Match</span>
          <div className="w-40">
            <SelectField
              value={v.match}
              onChange={(match) => onChange({ ...v, match })}
              options={[{ value: 'AND', label: 'all conditions' }, { value: 'OR', label: 'any condition' }]}
              ariaLabel="Match"
            />
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-1.5 py-1 text-[12px] text-[color:var(--text-muted)]">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading {entity} fields…
          </div>
        )}
        {err && <div role="alert" className="text-[11px] text-danger">{err}</div>}

        {(v.clauses || []).map((clause, idx) => (
          <ClauseRow
            key={idx}
            clause={clause}
            fields={schema?.fields || []}
            fieldsByKey={fieldsByKey}
            operatorsByType={schema?.operators_by_type || {}}
            operatorDefs={schema?.operator_defs || {}}
            entity={entity}
            eventName={eventName}
            onChange={(patch) => updateClause(idx, patch)}
            onRemove={() => removeClause(idx)}
          />
        ))}

        <button
          type="button"
          onClick={addClause}
          disabled={loading || !!err}
          className={ghostBtnCls}
        >
          <Plus className="w-3 h-3" /> Add condition
        </button>
      </div>
    </div>
  );
}

function EventNamePicker({ schema, eventName, onChange }) {
  const names = schema?.event_names || [];
  return (
    <div className={panelCls}>
      <div className="text-[12px] text-[color:var(--text-muted)]">Event</div>
      <SelectField
        value={eventName}
        onChange={onChange}
        searchable
        options={[{ value: '', label: 'Any event' }, ...names.map((n) => ({ value: n, label: n }))]}
        ariaLabel="Event"
      />
      <div className="text-[11px] text-[color:var(--text-muted)]">
        Pick an event to filter on its own properties, or leave it on any event.
      </div>
    </div>
  );
}

function ClauseRow({ clause, fields, fieldsByKey, operatorsByType, operatorDefs, entity, eventName, onChange, onRemove }) {
  const field = fieldsByKey[clause.field] || fields[0];
  const opList = operatorsByType[field?.type || 'string'] || ['eq', 'ne'];
  const opDef = operatorDefs[clause.op] || { value_type: 'scalar' };

  return (
    <div className="space-y-1.5 rounded-md bg-[color:var(--surface-sunken)] p-1.5">
      <div className="flex items-center gap-1.5">
        <div className="min-w-0 flex-1">
          <FieldPicker
            fields={fields}
            value={clause.field}
            onChange={(key) => {
              const next = fieldsByKey[key];
              const nextOps = operatorsByType[next?.type || 'string'] || ['eq', 'ne'];
              // If the current operator is not valid for the new field's type, reset it.
              const newOp = nextOps.includes(clause.op) ? clause.op : nextOps[0];
              onChange({ field: key, op: newOp, value: '' });
            }}
          />
        </div>
        <button type="button" onClick={onRemove} aria-label="Remove condition" title="Remove" className={removeBtnCls}>
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-start gap-1.5">
        <div className="w-[42%] shrink-0">
          <SelectField
            value={clause.op}
            onChange={(op) => onChange({ op })}
            options={opList.map((op) => ({ value: op, label: operatorDefs[op]?.label || op }))}
            ariaLabel="Operator"
          />
        </div>
        <div className="min-w-0 flex-1">
          {opDef.value_type === 'none' ? (
            <div className="px-2 py-1.5 text-[12px] text-[color:var(--text-disabled)]">No value needed</div>
          ) : (
            <ValueInput
              field={field}
              op={clause.op}
              valueType={opDef.value_type}
              value={clause.value}
              entity={entity}
              eventName={eventName}
              onChange={(value) => onChange({ value })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FieldPicker({ fields, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = fields.find((f) => f.key === value);

  const groups = useMemo(() => {
    const filtered = fields.filter((f) => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (f.key || '').toLowerCase().includes(q) || (f.label || '').toLowerCase().includes(q);
    });
    const out = {};
    for (const f of filtered) {
      const g = f.source === 'fixed' ? 'Common fields'
              : f.source === 'custom_properties' ? 'Custom properties'
              : f.source === 'metadata' ? 'Metadata'
              : f.source === 'attributes' || f.source === 'span_attributes' ? 'Attributes'
              : f.source === 'payload' ? 'Input / output'
              : 'Other';
      if (!out[g]) out[g] = [];
      out[g].push(f);
    }
    return out;
  }, [fields, query]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cx(inputCls, 'flex items-center justify-between gap-1 text-left')}
      >
        <span className="truncate text-[color:var(--text-primary)]">{selected?.label || selected?.key || 'Select field'}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)]" />
      </button>
      {open && (
        <div className={`${menuCls} max-h-72`}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fields"
            className="h-8 w-full border-b border-[color:var(--border-subtle)] bg-transparent px-2.5 text-[12px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] focus:outline-none"
            autoFocus
          />
          {Object.entries(groups).map(([gname, items]) => (
            <div key={gname} className="py-1">
              <div className="px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{gname}</div>
              {items.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => { onChange(f.key); setOpen(false); setQuery(''); }}
                  className={`${menuItemCls} flex items-center justify-between ${f.key === value ? 'bg-[color:var(--surface-hover)]' : ''}`}
                >
                  <span className="truncate text-[color:var(--text-primary)]">{f.label || f.key}</span>
                  <span className="ml-2 shrink-0 text-[11px] text-[color:var(--text-muted)]">{f.type}</span>
                </button>
              ))}
            </div>
          ))}
          {!Object.keys(groups).length && (
            <div className="px-2.5 py-3 text-[12px] text-[color:var(--text-muted)]">No matching fields.</div>
          )}
        </div>
      )}
    </div>
  );
}

function ValueInput({ field, op, valueType, value, entity, eventName, onChange }) {
  const type = field?.type || 'string';

  if (valueType === 'multi') {
    // Render as comma-separated list (simplest), with a help line.
    return (
      <div>
        <input
          type="text"
          value={Array.isArray(value) ? value.join(', ') : (value || '')}
          onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
          placeholder="value1, value2"
          className={inputCls}
        />
      </div>
    );
  }

  if (type === 'boolean') {
    return (
      <SelectField
        value={String(value ?? 'true')}
        onChange={(v) => onChange(v === 'true')}
        options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]}
        ariaLabel="Value"
      />
    );
  }
  if (type === 'datetime') {
    return (
      <input
        type="datetime-local"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    );
  }
  if (type === 'number') {
    return (
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className={inputCls}
      />
    );
  }

  // Default: string with sample-value suggestions
  return <SuggestedStringInput
    key={field?.key || ''}
    field={field}
    entity={entity}
    eventName={eventName}
    value={value}
    onChange={onChange}
  />;
}

/** Property-value APIs return plain strings or `{ value, count }` rows — normalize both. */
function sampleValueToString(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'object' && v !== null && 'value' in v) return String(v.value);
  return String(v);
}

function SuggestedStringInput({ field, entity, eventName, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [dynamic, setDynamic] = useState(null);
  const fieldKey = field?.key || '';

  // Always show inline sampleValues if present.
  const samples = useMemo(() => {
    const fromSchema = (field?.sampleValues || []).filter(Boolean);
    const fromApi = (dynamic || []).filter(Boolean);
    const merged = [...new Set([...fromSchema, ...fromApi].map(sampleValueToString).filter(Boolean))];
    return merged.slice(0, 50);
  }, [field, dynamic]);

  // Load property values whenever the selected field changes (not only on first focus).
  useEffect(() => {
    let cancelled = false;
    setDynamic(null);
    if (entity !== 'events' || !eventName) return undefined;
    const propName = String(fieldKey).replace(/^custom_properties\./, '');
    if (!propName) return undefined;
    getPropertyValues({ entity: 'events', event: eventName, property: propName, limit: 50 })
      .then((values) => { if (!cancelled) setDynamic(values); })
      .catch(() => { if (!cancelled) setDynamic([]); });
    return () => { cancelled = true; };
  }, [entity, eventName, fieldKey]);

  return (
    <div className="relative">
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={inputCls}
      />
      {open && samples.length > 0 && (
        <div className={`${menuCls} max-h-44`}>
          {samples.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
              className={menuItemCls}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
